import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, FilterBar, Pagination, Pill, SelectField, type Column } from "@atlas/ui/web";
import type { ArquivoTrocado } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { Corta, downloadAuthed } from "../prefeitura/_ui";
import { ModalRelatorio } from "../prefeitura/troca-arquivos-relatorio";
import { RetornoConsignatariaPainel } from "./retorno-consignataria";
import { LayoutChip, ehCodigoPadrao } from "../../components/LayoutChip";

/**
 * TROCA DE ARQUIVOS — visão da averbadora, todas as folhas.
 *
 * A mesma tela do portal da prefeitura, sem o escopo de uma só. É por aqui que a
 * averbadora responde "o retorno de agosto chegou? de quais folhas? por quem?"
 * sem entrar no portal de cada prefeitura.
 *
 * A coluna Folha só existe aqui — no lado da prefeitura ela seria sempre a mesma.
 */

const MESES = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const fmtCompetencia = (c: string) => `${MESES[Number(c.slice(4, 6))] ?? c} de ${c.slice(0, 4)}`;

const fmtQuando = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * Grade dos filtros. O `FilterBar` renderiza `children` como bloco solto —
 * sem isto cada select ocupa a largura inteira e os tres empilham.
 */
const GRADE_FILTROS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
};

export function AdminTrocaArquivos() {
  const [unidadeId, setPrefeituraId] = useState("");
  const [tipo, setTipo] = useState("");
  const [competencia, setCompetencia] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);
  // Excluído fica escondido por default e é revelado sob demanda: é auditoria,
  // e quem investiga "cadê o arquivo de julho" precisa ver que ele foi excluído,
  // por quem e por quê.
  const [verExcluidos, setVerExcluidos] = useState(false);
  // O registro cujo relatório está aberto. A averbadora vê os arquivos de TODAS
  // as folhas, e é ela quem responde "por que a conciliação desta não fechou?".
  const [relatorio, setRelatorio] = useState<ArquivoTrocado | null>(null);
  /** O retorno processado a partir do qual gerar o arquivo para a consignatária (#189). */
  const [retornoPara, setRetornoPara] = useState<ArquivoTrocado | null>(null);
  const [alinhando, setAlinhando] = useState<string | null>(null);
  const [separando, setSeparando] = useState<string | null>(null);
  const [baixandoTombamento, setBaixandoTombamento] = useState<string | null>(null);

  async function baixarTombamento(a: ArquivoTrocado) {
    setBaixandoTombamento(a.id);
    try {
      const blob = await atlas.admin.trocaArquivoTombamento(a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tombamento-${a.unidadeId}-${a.competencia}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha ao baixar o relatório de tombamento.");
    } finally {
      setBaixandoTombamento(null);
    }
  }

  /**
   * ALINHAR CRONOGRAMAS PELA ORIGEM — ensaio, leitura, e só então gravação.
   *
   * O ensaio não grava nada; o confirm mostra o que ele encontrou, e o operador
   * decide sobre números, não sobre um botão. Aplicar de novo não acha o que
   * alinhar: a operação é idempotente.
   */
  async function alinharCronogramas(a: ArquivoTrocado) {
    setAlinhando(a.id);
    try {
      const plano = await atlas.admin.alinharCronogramasPelaOrigem(a.id);
      if (plano.alinhados === 0 && plano.quarentena_com_posicao === 0) {
        alert(
          `Nada a alinhar em ${a.arquivoNome}.\n\n` +
            `${plano.contratos_do_arquivo} dívida(s) legada(s) nasceram deste arquivo: ${plano.ja_alinhados} já na posição da linha, ` +
            `${plano.terminais} encerrada(s)` + (plano.nao_conferem ? `, ${plano.nao_conferem} cuja linha não confere` : "") + ".",
        );
        return;
      }
      const ex = plano.exemplos.slice(0, 3)
        .map((e) => `  ADF ${e.adf}: ${e.de.parcelasPagas}/${e.de.totalParcelas} → ${e.para.parcelasPagas}/${e.para.totalParcelas}${e.quitado ? " (quita)" : ""}`)
        .join("\n");
      const segue = window.confirm(
        `ENSAIO — nada foi gravado.\n\n` +
          `${plano.contratos_do_arquivo} dívida(s) legada(s) nasceram deste arquivo.\n` +
          `  ${plano.alinhados} com a parcela diferente da linha de origem → serão alinhadas\n` +
          `  ${plano.quitados} delas estão na última parcela → saem quitadas\n` +
          `  ${plano.ja_alinhados} já na posição, ${plano.terminais} encerrada(s)\n` +
          `  ${plano.nao_conferem} cuja linha não confere → ficam intocadas\n` +
          `  ${plano.quarentena_com_posicao} desconto(s) em quarentena ganham a posição da linha\n\n` +
          `${ex}\n\nAplicar?`,
      );
      if (!segue) return;
      const r = await atlas.admin.alinharCronogramasPelaOrigem(a.id, { aplicar: true });
      alert(
        `Aplicado: ${r.alinhados} cronograma(s) alinhado(s), ${r.quitados} quitado(s), ` +
          `${r.quarentena_com_posicao} desconto(s) em quarentena com posição.\n\nO plano completo ficou guardado ao lado do pacote.`,
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha ao alinhar os cronogramas.");
    } finally {
      setAlinhando(null);
    }
  }

  /**
   * SEPARAR LINHAS REPETIDAS — mesmo roteiro de "Cronogramas": ensaio, leitura,
   * e só então gravação. Recupera a dívida que a quarentena juntou com a linha
   * idêntica anterior (ponto B da ADR-0012). Aplicar de novo não acha o que separar.
   */
  async function separarLinhasRepetidas(a: ArquivoTrocado) {
    setSeparando(a.id);
    try {
      const plano = await atlas.admin.separarLinhasIdenticas(a.id);
      if (plano.separadas === 0 && plano.posicao_corrigida === 0) {
        alert(
          `Nada a separar em ${a.arquivoNome}.\n\n` +
            `${plano.linhas_repetidas} linha(s) repetem outra do mesmo servidor: ${plano.ja_separadas} já com registro ou contrato próprio` +
            (plano.nao_separadas ? `, ${plano.nao_separadas} cuja origem não dá para afirmar` : "") + ".",
        );
        return;
      }
      const ex = plano.separadas_lista.slice(0, 5)
        .map((s) =>
          `  Mat. ${s.matricula}, verba ${s.codigoVerba}, R$ ${s.valor.toFixed(2)} (linha ${s.linha}, ${s.parcelaAtual}/${s.parcelasTotal}): ` +
          (s.promove ? "vira contrato" : s.semPromocao ? `fica ${s.situacao} — ${s.semPromocao}` : `fica ${s.situacao}`),
        )
        .join("\n");
      const segue = window.confirm(
        `ENSAIO — nada foi gravado.\n\n` +
          `${plano.linhas_repetidas} linha(s) repetem outra do mesmo servidor, verba e valor.\n` +
          `  ${plano.separadas} tinham sumido no registro da anterior → ganham registro próprio\n` +
          `  ${plano.promovidas} delas herdam o aceite → viram contrato\n` +
          `  ${plano.posicao_corrigida} registro(s) voltam à posição da própria linha\n` +
          `  ${plano.ja_separadas} já separadas, ${plano.nao_separadas} cuja origem não dá para afirmar → intocadas\n\n` +
          `${ex}\n\nAplicar?`,
      );
      if (!segue) return;
      const r = await atlas.admin.separarLinhasIdenticas(a.id, { aplicar: true });
      alert(
        `Aplicado: ${r.separadas} linha(s) separada(s), ${r.promovidas} contrato(s) criado(s), ` +
          `${r.posicao_corrigida} registro(s) com a posição corrigida.\n\nO plano completo ficou guardado ao lado do pacote.`,
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : "Falha ao separar as linhas repetidas.");
    } finally {
      setSeparando(null);
    }
  }

  const q = useQuery({
    queryKey: ["admin", "troca-arquivos", unidadeId, tipo, competencia, busca, verExcluidos, pagina, porPagina],
    queryFn: () => atlas.admin.trocaArquivos({
      prefeitura_id: unidadeId ? Number(unidadeId) : undefined,
      tipo: (tipo || undefined) as "movimento" | "retorno" | "carga" | "retorno_consignataria" | undefined,
      competencia: competencia || undefined,
      q: busca || undefined,
      incluir_excluidos: verExcluidos ? 1 : 0,
      offset: (pagina - 1) * porPagina,
      limit: porPagina,
    }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });

  const arquivos = q.data?.arquivos ?? [];
  const total = q.data?.total ?? 0;
  const facetas = q.data?.facetas ?? { competencias: [], atores: [], prefeituras: [] };

  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  // Colunas densas pelo mesmo motivo do portal da prefeitura: com tudo `nowrap`
  // e um nome de folha como "I.M.A.M.N. - Instituto de Meio Ambiente" ao lado de
  // um nome de arquivo longo, a tabela passava da tela e só dava para ler
  // arrastando de lado. `Corta` põe o teto no conteúdo — `width` na coluna é só
  // sugestão num `table-layout: auto`. O valor inteiro fica no `title`.
  const columns: Column<ArquivoTrocado>[] = [
    {
      key: "arquivo",
      header: "Arquivo",
      width: 200,
      render: (a) => (
        <div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
          {a.tipo === "movimento"
            ? <Pill variant="averbado">Movimento</Pill>
            : a.tipo === "carga"
              ? <Pill variant="pendente">Carga inicial</Pill>
              : a.tipo === "retorno_consignataria"
                ? <Pill variant="averbado">Retorno à consignatária</Pill>
                : <Pill variant="aceita">Retorno</Pill>}
          <Corta largura={188} titulo={a.arquivoNome} estilo={{ fontSize: 11, opacity: 0.7, fontFamily: "var(--font-mono)" }}>
            {a.arquivoNome || "—"}
          </Corta>
        </div>
      ),
    },
    {
      key: "folha",
      header: "Folha / período",
      width: 190,
      render: (a) => {
        const folha = a.prefeituraNome ?? `Organização ${a.unidadeId}`;
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <Corta largura={178} titulo={folha} estilo={{ fontSize: 13 }}>{folha}</Corta>
            <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" }}>{fmtCompetencia(a.competencia)}</span>
          </div>
        );
      },
    },
    {
      key: "inclusao",
      header: "Inclusão",
      width: 150,
      render: (a) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={138} titulo={a.atorNome} estilo={{ fontSize: 12 }}>{a.atorNome}</Corta>
          <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" }}>{fmtQuando(a.recebidoEm)}</span>
        </div>
      ),
    },
    {
      key: "observacao",
      header: "Observação",
      wrap: true,
      render: (a) => (
        <div style={{ display: "grid", gap: 2, maxWidth: 240 }}>
          <span>{a.observacao}</span>
          {a.parte ? <span style={{ fontSize: 11, opacity: 0.65 }}>Parte: {a.parte}</span> : null}
          {a.excluidoEm ? (
            <span style={{ fontSize: 11, color: "var(--gold-500)" }}>
              Excluído por {a.excluidoPor ?? "—"} — {a.excluidoMotivo ?? "sem motivo"}
            </span>
          ) : null}
          {a.status === "rejeitado" ? (
            <span style={{ fontSize: 11, color: "var(--danger, #d33)" }}>
              Recusado — {String(a.resumo?.motivo ?? "sem motivo registrado")}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "layout",
      header: "Layout",
      width: 190,
      wrap: true,
      // O REGISTRO GUARDA A VERSAO DA EPOCA, nao a do catalogo: o arquivo foi
      // lido sob aquela regra, e reler o passado com os olhos do presente e'
      // exatamente o que a vigencia por competencia existe para impedir.
      //
      // O CHIP E' O MESMO DO HUB E DA FICHA (FR-036) — e aqui com `comLink`,
      // porque quem pergunta "que layout leu este arquivo?" costuma querer a
      // ficha em seguida, e a averbadora enxerga essa tela.
      render: (a) =>
        a.layout ? (
          <LayoutChip
            tamanho="sm"
            comLink
            nome={a.layout.nome}
            codigo={a.layout.codigo}
            versao={a.layout.versao}
            padrao={ehCodigoPadrao(a.layout.codigo)}
          />
        ) : (
          <span style={{ opacity: 0.4 }} title="Registro anterior ao catálogo de layouts.">
            —
          </span>
        ),
    },
    {
      key: "acoes",
      header: "Ações",
      width: 170,
      render: (a) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {a.arquivoKey ? (
            <Button
              size="sm"
              variant="ghost"
              title="Baixa o arquivo que foi trocado naquele dia."
              onClick={() => downloadAuthed(atlas.admin.trocaArquivoUrl(a.id), a.arquivoNome)}
            >
              ↓ Baixar
            </Button>
          ) : null}
          {/* RETORNO À CONSIGNATÁRIA (#189): só de um retorno já processado —
              é dele que sai o que a folha descontou nas rubricas do credor. */}
          {a.tipo === "retorno" && a.status.startsWith("processado") ? (
            <Button
              size="sm"
              variant="ghost"
              title="Gera, para um credor com rubrica na unidade, o arquivo com o que esta folha descontou nas rubricas dele."
              onClick={() => setRetornoPara(a)}
            >
              Retorno à consignatária
            </Button>
          ) : null}
          {/* `temRelatorio` decide ANTES do clique. Um botao que abre para dar
              404 em registro legado seria pior que botao nenhum. */}
          {a.temRelatorio ? (
            <Button
              size="sm"
              variant="ghost"
              title={
                a.tipo === "movimento"
                  ? "O que a escrita no layout do órgão custou: dívidas somadas e campos sem lugar."
                  : "O que a tradução leu, gerou, descartou e derivou neste arquivo."
              }
              onClick={() => setRelatorio(a)}
            >
              Relatório
            </Button>
          ) : null}
          {a.tipo === "carga" && a.temTombamento ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={baixandoTombamento === a.id}
              title="Baixa as pendências, divergências e contratos processados na carga inicial."
              onClick={() => void baixarTombamento(a)}
            >
              {baixandoTombamento === a.id ? "Baixando…" : "Tombamento"}
            </Button>
          ) : null}
          {/* CRONOGRAMAS PELA ORIGEM: só carga com pacote guardado. O ensaio vem
              primeiro e o operador lê o plano antes de aplicar. */}
          {a.tipo === "carga" && a.temPacote !== false ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={alinhando === a.id}
              title="Relê a parcela de cada dívida legada na linha deste arquivo de onde ela saiu. Mostra o plano antes de gravar."
              onClick={() => void alinharCronogramas(a)}
            >
              {alinhando === a.id ? "Conferindo…" : "Cronogramas"}
            </Button>
          ) : null}
          {/* LINHAS REPETIDAS: carga ou retorno com pacote guardado — os dois
              passam pela quarentena. O ensaio vem primeiro. */}
          {(a.tipo === "carga" || a.tipo === "retorno") && a.temPacote !== false ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={separando === a.id}
              title="Recupera a dívida que a quarentena juntou com uma linha idêntica anterior deste arquivo. Mostra o plano antes de gravar."
              onClick={() => void separarLinhasRepetidas(a)}
            >
              {separando === a.id ? "Conferindo…" : "Linhas repetidas"}
            </Button>
          ) : null}
          {!a.arquivoKey && !a.temRelatorio ? <span style={{ opacity: 0.4 }}>—</span> : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: "1.6rem" }}>Troca de arquivos</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
          O que a Atlas enviou e o que cada folha devolveu, competência a competência.
        </p>
      </div>

      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        onReset={() => {
          setPrefeituraId(""); setTipo(""); setCompetencia(""); setBusca(""); setVerExcluidos(false); setPagina(1);
        }}
        actions={
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={verExcluidos}
              onChange={(e) => filtrar(setVerExcluidos)(e.target.checked)}
            />
            Mostrar excluídos
          </label>
        }
      >
        <div style={GRADE_FILTROS}>
        <SelectField
          label="Folha"
          value={unidadeId}
          onChange={(e) => filtrar(setPrefeituraId)(e.target.value)}
          options={[
            { value: "", label: "— TODAS —" },
            ...facetas.prefeituras.map((p) => ({ value: String(p.id), label: p.nome })),
          ]}
        />
        <SelectField
          label="Tipo"
          value={tipo}
          onChange={(e) => filtrar(setTipo)(e.target.value)}
          options={[
            { value: "", label: "— TODOS —" },
            { value: "movimento", label: "Movimento" },
            { value: "retorno", label: "Retorno" },
            { value: "carga", label: "Carga inicial" },
            { value: "retorno_consignataria", label: "Retorno à consignatária" },
          ]}
        />
        <SelectField
          label="Período"
          value={competencia}
          onChange={(e) => filtrar(setCompetencia)(e.target.value)}
          options={[
            { value: "", label: "— TODOS —" },
            ...facetas.competencias.map((c) => ({ value: c, label: fmtCompetencia(c) })),
          ]}
        />
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={arquivos}
        rowKey={(a) => a.id}
        loading={q.isLoading}
        emptyState="Nenhum arquivo trocado ainda."
      />

      <Pagination
        total={total}
        pagina={pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="registros"
      />

      {retornoPara ? (
        <RetornoConsignatariaPainel
          unidadeId={retornoPara.unidadeId}
          competencia={retornoPara.competencia}
          onClose={() => setRetornoPara(null)}
        />
      ) : null}

      {relatorio ? (
        <ModalRelatorio
          arquivo={relatorio}
          buscar={(id) => atlas.admin.trocaArquivoRelatorio(id)}
          onClose={() => setRelatorio(null)}
        />
      ) : null}
    </div>
  );
}
